import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { formatApiError } from "@/lib/api";
import { Truck } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await login(email, password);
      nav("/");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 relative bg-[var(--navy)]">
        <img
          src="https://images.unsplash.com/photo-1726866672851-5b99c837603c?crop=entropy&cs=srgb&fm=jpg&q=85"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
          alt="logistics"
        />
        <div className="relative z-10 p-14 flex flex-col justify-between text-white">
          <div className="flex items-center gap-2">
            <Truck size={22} className="text-amber-400" />
            <div className="font-heading font-extrabold text-xl">SRAD Dispatch Hub</div>
          </div>
          <div>
            <h1 className="font-heading text-4xl font-extrabold leading-tight">
              Dispatch. Notify.<br />Deliver — <span className="text-amber-400">Automated.</span>
            </h1>
            <p className="text-sm text-slate-300 mt-4 max-w-md">
              One dispatch entry → auto-notify every client contact through WhatsApp & email with Invoice + LR attached. Track the complete communication lifecycle.
            </p>
            <div className="text-xs text-slate-500 uppercase tracking-[0.2em] mt-8">
              SRAD SERVICES PRIVATE LIMITED · ProcurMart
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Welcome Back</div>
            <h2 className="font-heading text-3xl font-extrabold text-slate-900">Sign in to your account</h2>
            <p className="text-sm text-slate-500 mt-2">Enter your credentials to continue.</p>
          </div>

          {err && <div data-testid="login-error" className="mb-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{err}</div>}

          <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Email</label>
          <input
            data-testid="login-email"
            value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full mt-1 mb-4 px-3 py-2.5 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm"
            type="email" required
          />
          <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Password</label>
          <input
            data-testid="login-password"
            value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full mt-1 mb-6 px-3 py-2.5 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm"
            type="password" required
          />
          <button
            data-testid="login-submit"
            disabled={loading}
            className="w-full bg-[var(--navy)] hover:bg-slate-800 text-white text-sm font-semibold py-2.5 rounded-md transition-colors disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
          <div className="mt-4 text-center">
            <a data-testid="link-forgot" href="/forgot-password" className="text-xs text-slate-600 hover:text-slate-900 font-semibold">Forgot password?</a>
          </div>
        </form>
      </div>
    </div>
  );
}
